import React, { useEffect, useMemo } from "react";
import { useField } from "formik";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import ReactSelect from "react-select";
import Field from "./ui/Field";
import Toggle from "./ui/Toggle";
import { useFormOptions } from "./FormOptionsContext";
import { useMultiIdField, useSingleIdField } from "./useSelectField";
import { createSelectStyles, selectTheme } from "./ui/selectStyles";
import MediaSection from "./sections/MediaSection";

const CreateLink = ({ entity, label }) => (
  <Link
    to={`/catalog?create=${entity}`}
    className="pf-field__action"
    title={label}
  >
    <Plus size={12} aria-hidden="true" />
    New
  </Link>
);

const STATUS_OPTIONS = [
  { value: 1, label: "Active" },
  { value: 2, label: "Inactive" },
];

const StatusField = () => {
  const [, meta, helpers] = useField("status");
  const value = useMemo(
    () => STATUS_OPTIONS.find((o) => o.value === meta.value) ?? STATUS_OPTIONS[0],
    [meta.value],
  );
  return (
    <Field label="Status" error={meta.touched ? meta.error : null}>
      <ReactSelect
        inputId="status"
        options={STATUS_OPTIONS}
        value={value}
        onChange={(o) => helpers.setValue(o.value)}
        onBlur={() => helpers.setTouched(true)}
        styles={createSelectStyles()}
        theme={selectTheme}
      />
    </Field>
  );
};

const B2bField = () => {
  const [, meta, helpers] = useField("is_b2b");
  return (
    <Toggle
      name="is_b2b"
      checked={meta.value === 1}
      onChange={(v) => helpers.setValue(v ? 1 : 0)}
      label="B2B Sourced Product"
      hint="Mark products supplied through business wholesale channels."
    />
  );
};

const OrganizationRail = ({ mode = "create" }) => {
  const {
    categoryOptions,
    subcategoryOptions,
    isLoadingSubcategories,
    brandOptions,
    typeOptions,
    isLoadingTypes,
  } = useFormOptions();

  const category = useMultiIdField("category_ids", categoryOptions);
  const subCategory = useMultiIdField("sub_category_ids", subcategoryOptions);
  const brand = useMultiIdField("brand_ids", brandOptions);
  const type = useSingleIdField("type_id", typeOptions);

  const hasCategory = category.value.length > 0;
  const hasSubCategory = subCategory.value.length > 0;

  // Sub-categories are scoped to the selected categories. Drop any held
  // sub_category_ids that aren't valid for the current category selection —
  // useMultiIdField hides them visually but Formik would still submit them.
  const [subCategoryIdsField, , subCategoryIdsHelpers] = useField("sub_category_ids");
  const subCategoryIdsValue = subCategoryIdsField.value;
  useEffect(() => {
    if (isLoadingSubcategories) return;
    if (!Array.isArray(subCategoryIdsValue) || subCategoryIdsValue.length === 0) return;
    const validIds = new Set(subcategoryOptions.map((o) => o.value));
    const filtered = subCategoryIdsValue.filter((id) => validIds.has(id));
    if (filtered.length !== subCategoryIdsValue.length) {
      subCategoryIdsHelpers.setValue(filtered);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subCategoryIdsValue, subcategoryOptions, isLoadingSubcategories]);

  // Types are scoped to the selected sub-categories. If the current type is no
  // longer valid for the chosen sub-categories (or the sub-category was
  // cleared), drop it — otherwise the product would carry a type the backend
  // rejects with "Selected type is not allowed for the chosen sub-categories."
  const [typeIdField, , typeIdHelpers] = useField("type_id");
  const typeIdValue = typeIdField.value;
  useEffect(() => {
    if (isLoadingTypes) return;
    if (typeIdValue == null) return;
    if (!typeOptions.some((o) => o.value === typeIdValue)) {
      typeIdHelpers.setValue(null);
    }
    // typeIdHelpers identity is stable per Formik; exclude to avoid loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeIdValue, typeOptions, isLoadingTypes]);

  return (
    <aside>
      <MediaSection />

      {mode === "edit" ? (
        <div className="pf-rail-card" style={{ marginTop: 16 }}>
          <StatusField />
        </div>
      ) : null}

      <div className="pf-rail-card" style={{ marginTop: 16 }}>
        <div className="pf-rail-card__title">Placement</div>

        <Field
          label="Category"
          required
          error={category.error}
          action={<CreateLink entity="category" label="Create new category" />}
        >
          <ReactSelect
            inputId="category_ids"
            isMulti
            placeholder="Select categories"
            options={categoryOptions}
            value={category.value}
            onChange={category.onChange}
            onBlur={category.onBlur}
            styles={createSelectStyles({ invalid: category.isInvalid })}
            theme={selectTheme}
          />
        </Field>

        <Field
          label="Sub Category"
          error={subCategory.error}
          action={<CreateLink entity="subcategory" label="Create new sub category" />}
          hint={
            !hasCategory
              ? "Select a category first"
              : isLoadingSubcategories
              ? "Loading sub categories…"
              : undefined
          }
        >
          <ReactSelect
            inputId="sub_category_ids"
            isMulti
            placeholder={
              hasCategory ? "Select sub categories" : "Select a category first"
            }
            isDisabled={!hasCategory}
            isLoading={isLoadingSubcategories}
            options={subcategoryOptions}
            value={subCategory.value}
            onChange={subCategory.onChange}
            onBlur={subCategory.onBlur}
            styles={createSelectStyles({ invalid: subCategory.isInvalid })}
            theme={selectTheme}
          />
        </Field>

        <Field
          label="Product Type"
          error={type.error}
          action={<CreateLink entity="type" label="Create new product type" />}
          hint={
            !hasSubCategory
              ? "Select a sub category first"
              : isLoadingTypes
              ? "Loading available types…"
              : typeOptions.length === 0
              ? "No types available for the selected sub categories"
              : undefined
          }
        >
          <ReactSelect
            inputId="type_id"
            isClearable
            placeholder={
              hasSubCategory ? "Select a type" : "Select a sub category first"
            }
            isDisabled={!hasSubCategory || typeOptions.length === 0}
            isLoading={isLoadingTypes}
            options={typeOptions}
            value={type.value}
            onChange={type.onChange}
            onBlur={type.onBlur}
            styles={createSelectStyles({ invalid: type.isInvalid })}
            theme={selectTheme}
          />
        </Field>

        <Field label="Brand" required error={brand.error}>
          <ReactSelect
            inputId="brand_ids"
            isMulti
            placeholder="Select brands"
            options={brandOptions}
            value={brand.value}
            onChange={brand.onChange}
            onBlur={brand.onBlur}
            styles={createSelectStyles({ invalid: brand.isInvalid })}
            theme={selectTheme}
          />
        </Field>
      </div>

      <div className="pf-rail-card" style={{ marginTop: 16 }}>
        <div className="pf-rail-card__title">Sourcing</div>
        <B2bField />
      </div>
    </aside>
  );
};

export default React.memo(OrganizationRail);
